import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Sparkles,
  Send,
  Paperclip,
  ArrowLeft,
  FileText,
  Loader2,
  CheckCircle2,
  AlertCircle,
  X,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  createCase,
  getCase,
  sendChat,
  summarizeCase,
  saveContact,
  uploadDocument,
} from "@/lib/api";
import CaseSummary from "@/components/CaseSummary";
import ContactForm from "@/components/ContactForm";

const ACCEPT =
  ".pdf,.jpg,.jpeg,.png,.docx,.xlsx,.doc,.mp4,application/pdf,image/jpeg,image/png,image/jpg,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,video/mp4";

export default function IntakePage() {
  const [searchParams] = useSearchParams();
  // SMS funnel hands off via /intake?case_id=xxx so the user keeps their
  // existing lead record (with phone/contact already captured) instead of
  // starting fresh. Falls back to creating a new case when no param is set.
  const resumeCaseId = searchParams.get("case_id");
  // Phase 2B.3 — language preference (en|es). Pre-selected via ?lang=es,
  // otherwise sticky in localStorage, otherwise English.
  const queryLang = (searchParams.get("lang") || "").toLowerCase();
  const initialLang =
    queryLang === "es" || queryLang === "en"
      ? queryLang
      : (typeof window !== "undefined" && localStorage.getItem("lexi.lang")) || "en";
  const [language, setLanguage] = useState(initialLang === "es" ? "es" : "en");
  const t = (en, es) => (language === "es" ? es : en);
  const [caseId, setCaseId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [readyForSummary, setReadyForSummary] = useState(false);
  const [summary, setSummary] = useState(null);
  const [summarizing, setSummarizing] = useState(false);
  const [contactSaved, setContactSaved] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [stage, setStage] = useState("chat"); // chat | summary | contact | done
  const [error, setError] = useState(null);

  const chatBottomRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    chatBottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, sending]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        // Resume an existing case (SMS funnel hand-off) if case_id is in the
        // URL; otherwise spin up a fresh one. We hide the contact form later
        // when contact data already exists on the resumed case.
        const data = resumeCaseId
          ? await getCase(resumeCaseId)
          : await createCase(language);
        if (!mounted) return;
        setCaseId(data.id || data.case_id);
        setMessages(data.messages || []);
        if (data.language) {
          setLanguage(data.language);
          if (typeof window !== "undefined") {
            localStorage.setItem("lexi.lang", data.language);
          }
        }
        if (data.contact && data.contact.phone) {
          setContactSaved(true);
        }
      } catch (e) {
        setError("Could not start a new case. Please try again.");
      }
    })();
    return () => {
      mounted = false;
    };
  }, [resumeCaseId]); // eslint-disable-line react-hooks/exhaustive-deps
  // `language` is captured at effect-creation time on purpose — changing it
  // is handled by a full page reload (?lang=…) so we never want this effect
  // to refire on language flip.

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text || !caseId || sending) return;
    setInput("");
    setMessages((prev) => [
      ...prev,
      { role: "user", content: text, timestamp: new Date().toISOString() },
    ]);
    setSending(true);
    try {
      const data = await sendChat(caseId, text);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: data.reply,
          timestamp: new Date().toISOString(),
        },
      ]);
      if (data.ready_for_summary) setReadyForSummary(true);
    } catch (e) {
      toast.error("Lexi couldn't process that. Please try again.");
      setMessages((prev) => prev.slice(0, -1));
      setInput(text);
    } finally {
      setSending(false);
    }
  }, [caseId, input, sending]);

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFiles = async (files) => {
    if (!caseId || !files?.length) return;
    setUploading(true);
    for (const file of Array.from(files)) {
      try {
        const doc = await uploadDocument(caseId, file);
        setDocuments((prev) => [...prev, doc]);
        toast.success(`Uploaded ${doc.original_filename}`);
      } catch (e) {
        toast.error(
          `${file.name}: ${e?.response?.data?.detail || "upload failed"}`
        );
      }
    }
    setUploading(false);
  };

  const handleSummarize = async () => {
    if (!caseId || summarizing) return;
    setSummarizing(true);
    try {
      const data = await summarizeCase(caseId);
      setSummary(data.summary);
      setStage("summary");
    } catch (e) {
      toast.error("Could not generate summary. Please try again.");
    } finally {
      setSummarizing(false);
    }
  };

  const handleContactSubmit = async (contact) => {
    if (!caseId) return;
    try {
      await saveContact(caseId, contact);
      setContactSaved(true);
      setStage("done");
    } catch (e) {
      toast.error(
        e?.response?.data?.detail?.[0]?.msg ||
          e?.response?.data?.detail ||
          "Could not save contact"
      );
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-6">
        <div className="text-center" data-testid="intake-error">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto" />
          <p className="mt-4 text-slate-700">{error}</p>
          <Link to="/" className="mt-4 inline-block text-[#0A192F] underline">
            Back to home
          </Link>
        </div>
      </div>
    );
  }

  if (stage === "done") {
    return <DoneScreen />;
  }

  if (stage === "contact") {
    return (
      <ContactScreen
        onBack={() => setStage("summary")}
        onSubmit={handleContactSubmit}
      />
    );
  }

  if (stage === "summary" && summary) {
    return (
      <SummaryScreen
        summary={summary}
        documents={documents}
        onContinue={() => setStage("contact")}
        onBack={() => setStage("chat")}
      />
    );
  }

  return (
    <div
      data-testid="intake-page"
      className="min-h-screen bg-[#F8FAFC] flex flex-col"
    >
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link
            to="/"
            data-testid="intake-back-link"
            className="flex items-center gap-2 text-slate-700 hover:text-[#0A192F]"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-medium">{t("Exit intake", "Salir")}</span>
          </Link>
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-full bg-[#0A192F] flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-[#C5A059]" />
            </span>
            <div className="leading-tight">
              <p className="font-heading text-sm font-semibold text-[#0A192F]">
                Lexi
              </p>
              <p className="text-xs text-slate-500">
                {t("Legal intake assistant", "Asistente de admisión legal")}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Language toggle — only shows pre-chat to avoid mid-conversation switches */}
            {messages.length <= 1 && (
              <div
                data-testid="intake-lang-toggle"
                className="inline-flex text-xs rounded-full border border-slate-200 bg-white overflow-hidden"
              >
                {[
                  { code: "en", label: "EN" },
                  { code: "es", label: "ES" },
                ].map((opt) => (
                  <button
                    key={opt.code}
                    type="button"
                    data-testid={`intake-lang-${opt.code}`}
                    onClick={() => {
                      if (opt.code === language) return;
                      setLanguage(opt.code);
                      if (typeof window !== "undefined") {
                        localStorage.setItem("lexi.lang", opt.code);
                      }
                      // Reload the intake with the new language so a fresh
                      // case is created in the chosen tongue.
                      window.location.search = `?lang=${opt.code}`;
                    }}
                    className={`px-3 py-1 font-semibold tracking-wider transition ${
                      language === opt.code
                        ? "bg-[#0A192F] text-white"
                        : "text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
            <Button
              data-testid="intake-generate-summary-btn"
              onClick={handleSummarize}
              disabled={
                (!readyForSummary && messages.length < 4) || summarizing
              }
              className="bg-[#C5A059] hover:bg-[#D4AF37] text-[#0A192F] font-semibold rounded-lg disabled:opacity-40"
            >
              {summarizing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t("Building summary…", "Generando resumen…")}
                </>
              ) : (
                t("Generate Case Summary", "Generar resumen del caso")
              )}
            </Button>
          </div>
        </div>
      </header>

      {/* Chat scroll area */}
      <div className="flex-1 overflow-y-auto chat-scroll">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-4">
          {messages.map((m, idx) => (
            <ChatBubble
              key={`${m.timestamp || idx}-${m.role}`}
              role={m.role}
              content={m.content}
            />
          ))}
          {sending && <TypingBubble />}
          {documents.length > 0 && (
            <div
              data-testid="uploaded-docs-list"
              className="bg-white border border-slate-200 rounded-lg p-4"
            >
              <p className="text-xs tracking-[0.18em] uppercase font-semibold text-slate-500 mb-2">
                {t("Uploaded documents", "Documentos subidos")}
              </p>
              <ul className="space-y-2">
                {documents.map((d) => (
                  <li
                    key={d.id}
                    className="flex items-center gap-2 text-sm text-slate-700"
                  >
                    <FileText className="w-4 h-4 text-[#C5A059]" />
                    {d.original_filename}
                    <span className="text-xs text-slate-400 ml-auto">
                      {Math.round((d.size || 0) / 1024)} KB
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {readyForSummary && (
            <div
              data-testid="ready-banner"
              className="bg-[#0A192F]/5 border border-[#C5A059]/40 rounded-lg p-4 flex items-start gap-3"
            >
              <CheckCircle2 className="w-5 h-5 text-[#C5A059] mt-0.5" />
              <div className="flex-1">
                <p className="font-heading font-semibold text-[#0A192F]">
                  {t("Lexi has enough information.", "Lexi tiene suficiente información.")}
                </p>
                <p className="text-sm text-slate-600">
                  {t(
                    'Click "Generate Case Summary" above when you\'re ready.',
                    'Cuando estés listo, haz clic en "Generar resumen del caso" arriba.'
                  )}
                </p>
              </div>
            </div>
          )}
          <div ref={chatBottomRef} />
        </div>
      </div>

      {/* Composer */}
      <div className="bg-white border-t border-slate-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4">
          <div className="bg-[#F8FAFC] border border-slate-200 rounded-xl p-3 focus-within:border-[#0A192F] focus-within:ring-2 focus-within:ring-[#0A192F]/15 transition">
            <Textarea
              data-testid="intake-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder={t("Tell Lexi what happened…", "Cuéntale a Lexi qué pasó…")}
              rows={2}
              className="resize-none border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 p-1 text-[15px] text-slate-800 placeholder:text-slate-400"
            />
            <div className="flex items-center justify-between pt-2">
              <div className="flex items-center gap-2">
                <input
                  ref={fileInputRef}
                  data-testid="intake-file-input"
                  type="file"
                  multiple
                  accept={ACCEPT}
                  className="hidden"
                  onChange={(e) => handleFiles(e.target.files)}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  data-testid="intake-attach-btn"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading || !caseId}
                  className="text-slate-600 hover:text-[#0A192F]"
                >
                  {uploading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Paperclip className="w-4 h-4 mr-2" />
                  )}
                  Attach document
                </Button>
                <span className="text-xs text-slate-400">
                  PDF · JPG · PNG · DOCX · XLSX · MP4 · max 50MB
                </span>
              </div>
              <Button
                data-testid="intake-send-btn"
                onClick={handleSend}
                disabled={!input.trim() || sending}
                className="bg-[#0A192F] hover:bg-[#112A46] text-white rounded-lg disabled:opacity-40"
              >
                {sending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    Send
                    <Send className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </div>
          <p
            data-testid="intake-disclaimer"
            className="mt-3 text-xs text-slate-500 text-center"
          >
            LexIntake is not a law firm and does not provide legal advice. No
            attorney-client relationship is formed.
          </p>
        </div>
      </div>
    </div>
  );
}

function ChatBubble({ role, content }) {
  const isUser = role === "user";
  return (
    <div
      data-testid={`msg-${role}`}
      className={`msg-in flex ${isUser ? "justify-end" : "justify-start"}`}
    >
      {!isUser && (
        <span className="w-8 h-8 rounded-full bg-[#0A192F] flex items-center justify-center mr-3 shrink-0 mt-1">
          <Sparkles className="w-3.5 h-3.5 text-[#C5A059]" />
        </span>
      )}
      <div
        className={`max-w-[80%] rounded-2xl px-4 py-3 text-[15px] leading-relaxed whitespace-pre-wrap ${
          isUser
            ? "bg-[#0A192F] text-white rounded-tr-md"
            : "bg-white border border-slate-200 text-slate-800 rounded-tl-md"
        }`}
      >
        {content}
      </div>
    </div>
  );
}

function TypingBubble() {
  return (
    <div data-testid="typing-bubble" className="flex justify-start msg-in">
      <span className="w-8 h-8 rounded-full bg-[#0A192F] flex items-center justify-center mr-3 shrink-0 mt-1">
        <Sparkles className="w-3.5 h-3.5 text-[#C5A059]" />
      </span>
      <div className="bg-white border border-slate-200 rounded-2xl rounded-tl-md px-4 py-3">
        <span className="typing-dot" />
        <span className="typing-dot" />
        <span className="typing-dot" />
      </div>
    </div>
  );
}

function SummaryScreen({ summary, documents, onContinue, onBack }) {
  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <button
            data-testid="summary-back-btn"
            onClick={onBack}
            className="flex items-center gap-2 text-slate-700 hover:text-[#0A192F]"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-medium">Back to chat</span>
          </button>
          <Button
            data-testid="summary-continue-btn"
            onClick={onContinue}
            className="bg-[#0A192F] hover:bg-[#112A46] text-white rounded-lg"
          >
            Continue — add contact info
          </Button>
        </div>
      </header>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <CaseSummary summary={summary} documents={documents} />
      </div>
    </div>
  );
}

function ContactScreen({ onBack, onSubmit }) {
  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <button
            data-testid="contact-back-btn"
            onClick={onBack}
            className="flex items-center gap-2 text-slate-700 hover:text-[#0A192F]"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-medium">Back to summary</span>
          </button>
          <span className="text-xs tracking-[0.18em] uppercase font-semibold text-slate-500">
            Final step
          </span>
        </div>
      </header>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12">
        <ContactForm onSubmit={onSubmit} />
      </div>
    </div>
  );
}

function DoneScreen() {
  return (
    <div
      data-testid="intake-done"
      className="min-h-screen bg-white flex items-center justify-center p-6"
    >
      <div className="max-w-lg text-center">
        <span className="w-16 h-16 mx-auto rounded-full bg-[#0A192F] flex items-center justify-center">
          <CheckCircle2 className="w-8 h-8 text-[#C5A059]" />
        </span>
        <h1 className="mt-6 font-heading text-3xl font-bold text-[#0A192F]">
          Your case is organized.
        </h1>
        <p className="mt-3 text-slate-600 leading-relaxed">
          We&apos;ve sent your attorney-ready case file into our review queue.
          Approved attorneys in your state and practice area will be matched
          to your case. Expect to hear from a qualified attorney soon.
        </p>
        <p className="mt-6 text-xs text-slate-500 leading-relaxed">
          LexIntake is not a law firm and does not provide legal advice. No
          attorney-client relationship is formed.
        </p>
        <Link to="/">
          <Button
            data-testid="done-home-btn"
            className="mt-8 bg-[#0A192F] hover:bg-[#112A46] text-white rounded-lg px-6 py-5"
          >
            Back to home
          </Button>
        </Link>
      </div>
    </div>
  );
}
